import { Search, Sprout, Activity, Stethoscope, ArrowDown } from 'lucide-react';
import FadeInSection from '../common/FadeInSection';
import { Link } from 'react-router-dom';

const steps = [
  {
    id: 1,
    title: "Find Your Plants",
    desc: "Input your environment details to discover the perfect flora matches.",
    url: "/find-plants",
    icon: <Search size={32} />,
    color: "#D68C21" // Sunflower Gold
  },
  {
    id: 2,
    title: "Build Your Garden",
    desc: "Add selected plants to your dashboard to manage your collection.",
    url: "/dashboard",
    icon: <Sprout size={32} />,
    color: "#556B2F" // Olive Green
  },
  {
    id: 3,
    title: "Track Eco Impact",
    desc: "Visualize the real-world environmental benefits of your garden.",
    url: "/report",
    icon: <Activity size={32} />,
    color: "#8C9A5B" // Soft Sage
  },
  {
    id: 4,
    title: "Plant Doctor",
    desc: "Upload photos of sick plants to get instant AI diagnosis and advice.",
    url: "/ai-doctor",
    icon: <Stethoscope size={32} />,
    color: "#3E2723" // Deep Earth
  }
];

const Instruction = ({ onScrollClick }) => {
  return (
    <>
      <style>{`
        {/* @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap'); */}

        :root {
          --sunflower-gold: #D68C21;
          --deep-earth: #3E2723;
          --warm-cream: #FEFDF5;
          --soft-sage: #8C9A5B;
        }

        .fade-in-section {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .fade-in-section.is-visible {
          opacity: 1;
          transform: none;
        }

        .instruction-container {
          background-color: #FFF9E6;
          padding: 3rem 2rem 8rem 2rem; /* Extra padding bottom for arrow */
          position: relative;
          overflow: hidden;
        }

        .instruction-content {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-header h2 {
          font-family: 'Playfair Display', serif;
          color: var(--deep-earth);
          font-size: 3rem;
          margin-bottom: 0.5rem;
        }

        .section-header p {
          font-family: 'Lato', sans-serif;
          color: #6D4C41;
          font-size: 1.2rem;
        }

        /* --- ROADMAP STYLES --- */
        .roadmap-wrapper {
          position: relative;
          padding: 2rem 0;
        }

        /* The Path (Line) */
        .roadmap-line {
          position: absolute;
          top: 50%;
          left: 0;
          width: 100%;
          height: 4px;
          background: repeating-linear-gradient(
            90deg,
            #D68C21,
            #D68C21 10px,
            transparent 10px,
            transparent 20px
          ); /* Dashed Line Effect */
          transform: translateY(-50%);
          z-index: 0;
          opacity: 0.4;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2rem;
          position: relative;
          z-index: 1;
        }

        /* Individual Card */
        .step-card-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
        }

        /* The Number Circle on the line */
        .step-marker {
          width: 50px;
          height: 50px;
          background-color: var(--warm-cream);
          border: 3px solid var(--sunflower-gold);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', serif;
          font-weight: 700;
          color: var(--deep-earth);
          font-size: 1.2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 0 0 5px var(--warm-cream); /* Clear space around line */
          transition: all 0.3s ease;
        }

        /* The Card Box */
        .step-box {
          background: var(--warm-cream);
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(62, 39, 35, 0.05);
          border: 1px solid rgba(214, 140, 33, 0.15);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          cursor: pointer;
          text-decoration: none;
        }

        .step-box:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 35px rgba(214, 140, 33, 0.15);
          border-color: var(--sunflower-gold);
        }

        .step-box:hover .step-marker-ref {
          background-color: var(--sunflower-gold);
          color: white;
        }

        .icon-wrapper {
          margin-bottom: 1rem;
          color: var(--sunflower-gold);
          background: #FFF8E1;
          padding: 12px;
          border-radius: 50%;
        }

        .step-title {
          font-family: 'Playfair Display', serif;
          color: var(--deep-earth);
          font-size: 1.25rem;
          margin-bottom: 0.8rem;
          font-weight: 700;
        }

        .step-desc {
          font-family: 'Lato', sans-serif;
          color: #666;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        /* --- BOUNCING ARROW --- */
        .arrow-container {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          color: var(--deep-earth);
          cursor: pointer;
          transition: color 0.3s;
        }
        
        .arrow-container:hover {
          color: var(--sunflower-gold);
        }

        .bounce-icon {
          animation: bounce 2s infinite;
        }

        .arrow-text {
          margin-top: 0.5rem;
          font-weight: 700;
          font-family: 'Lato', sans-serif;
          font-size: 0.9rem;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        /* Responsive */
        @media (max-width: 900px) {
          .steps-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          
          /* Change line to vertical for mobile */
          .roadmap-line {
            width: 4px;
            height: 100%;
            top: 0;
            left: 50%;
            background: repeating-linear-gradient(
              180deg,
              #D68C21,
              #D68C21 10px,
              transparent 10px,
              transparent 20px
            );
            transform: translateX(-50%);
          }
          
          .step-marker {
            margin-bottom: 1rem;
          }

          {/* .instruction-container {
             padding-bottom: 10rem;
          } */}
        }
      `}</style>

      <div className="instruction-container" id="homepage-instruction-section">
        <div className="instruction-content">
          
          <FadeInSection>
            <div className="section-header">
              <h2>Your Green Journey</h2>
              <p>Four simple steps to make a lasting impact.</p>
            </div>
          </FadeInSection>

          <div className="roadmap-wrapper">
            {/* The Dashed Connecting Line */}
            <div className="roadmap-line"></div>

            <div className="steps-grid">
              {steps.map((step, index) => (
                <FadeInSection key={step.id} delay={`${index * 0.15}s`}>
                  <div className="step-card-container">
                    
                    {/* The Number Marker (sits on the line) */}
                    <div className="step-marker step-marker-ref">
                      {step.id}
                    </div>

                    {/* The Card */}
                    <Link to={step.url} className="step-box">
                      <div className="icon-wrapper" style={{ color: step.color, backgroundColor: `${step.color}15` }}>
                        {step.icon}
                      </div>
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-desc">{step.desc}</p>
                    </Link>

                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>

        </div>

        {/* Bottom Action Arrow */}
        <div className="arrow-container" onClick={() => onScrollClick && onScrollClick('homepage-eventscomm-section')}>
           <div className="bounce-icon">
             <ArrowDown size={32} />
           </div>
           <div className="arrow-text">Explore Events</div>
        </div>

      </div>
    </>
  );
};

export default Instruction;